// Bounded so a slow or unreachable analytics endpoint can never turn into an
// unbounded queue in the browser tab. Dropping is intentional — a lost event
// is cheap, a stall anywhere in the booking flow is not. Mirrors the shape of
// server/src/analytics/emitter.ts (same cap/flush-in-flight approach), split
// for the browser: sessionStorage persistence and a sendBeacon path for
// pagehide/visibilitychange instead of a Node interval timer.
import { CONTRACT_VERSION } from '@suitescanner/analytics-contract';
import type { ClientEventMap } from '@suitescanner/analytics-contract';
import { getAnonId, getSessionId } from '~/lib/identity';

type ClientEventName = keyof ClientEventMap;

type QueuedEvent = {
    eventId: string;
    name: ClientEventName;
    occurredAt: string;
    props: unknown;
};

type WebStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

const BUFFER_CAP = 200;
const FLUSH_AT_SIZE = 20;
const FLUSH_INTERVAL_MS = 5_000;
const STORAGE_KEY = 'suitescanner-analytics-queue';
const COLLECT_PATH = '/v1/collect';

let buffer: QueuedEvent[] = [];
let droppedSinceLastFlush = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight = false;
let listenersAttached = false;
let restoredFromStorage = false;

function sessionStorageRef(): WebStorage | null {
    try {
        return (globalThis as { sessionStorage?: WebStorage }).sessionStorage ?? null;
    } catch {
        return null;
    }
}

function persist(): void {
    try {
        sessionStorageRef()?.setItem(STORAGE_KEY, JSON.stringify(buffer));
    } catch {
        // Quota or private-mode refusal — the in-memory buffer still serves this tab.
    }
}

// Runs once per module lifetime, lazily, so a reload during the funnel
// resumes whatever was still pending rather than dropping it.
function restore(): void {
    if (restoredFromStorage) return;
    restoredFromStorage = true;

    try {
        const raw = sessionStorageRef()?.getItem(STORAGE_KEY);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            buffer = parsed.slice(0, BUFFER_CAP);
        }
    } catch {
        // Corrupt or inaccessible storage — resume with an empty buffer.
    }
}

function safeEventId(): string {
    try {
        const cryptoApi = globalThis.crypto;
        if (typeof cryptoApi?.randomUUID === 'function') {
            return cryptoApi.randomUUID();
        }
    } catch {
        // fall through to the manual fallback below
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : (random & 0x3 | 0x8);

        return value.toString(16);
    });
}

function resolveUrl(): string | null {
    try {
        const raw = process.env.EXPO_PUBLIC_ANALYTICS_URL;
        return raw ? raw.replace(/\/$/, '') : null;
    } catch {
        return null;
    }
}

function attachUnloadFlush(): void {
    if (listenersAttached) return;
    listenersAttached = true;

    try {
        if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') void flush(true);
            });
        }
        if (typeof globalThis.addEventListener === 'function') {
            globalThis.addEventListener('pagehide', () => void flush(true));
        }
    } catch {
        // No DOM in this environment (native runtime, static export, tests).
    }
}

function scheduleFlush(): void {
    if (flushTimer) return;

    try {
        flushTimer = setTimeout(() => {
            flushTimer = null;
            void flush();
        }, FLUSH_INTERVAL_MS);
    } catch {
        flushTimer = null;
    }
}

function buildEnvelope(events: QueuedEvent[]) {
    return {
        contractVersion: CONTRACT_VERSION,
        source: 'client' as const,
        sentAt: new Date().toISOString(),
        context: {
            anonId: getAnonId(),
            sessionId: getSessionId(),
            platform: 'web' as const
        },
        events
    };
}

function trySendBeacon(url: string, body: string): boolean {
    try {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            return false;
        }
        const blob = new Blob([body], { type: 'application/json' });
        return navigator.sendBeacon(`${url}${COLLECT_PATH}`, blob);
    } catch {
        return false;
    }
}

// One flush in flight at a time — a slow endpoint must make the buffer grow
// toward its cap, never pile up concurrent outbound requests. A batch stays
// in the buffer until it is actually handed off, so a failed send is retried
// by the next scheduled flush instead of being silently lost.
async function flush(beacon = false): Promise<void> {
    if (flushInFlight || buffer.length === 0) return;

    const url = resolveUrl();
    if (!url) return;

    flushInFlight = true;

    try {
        const batch = buffer.slice(0, buffer.length);
        let body: string;

        try {
            body = JSON.stringify(buildEnvelope(batch));
        } catch {
            // Unserialisable props would fail on every retry too — drop this
            // batch rather than block every event behind it forever.
            buffer = buffer.slice(batch.length);
            persist();
            return;
        }

        if (beacon && trySendBeacon(url, body)) {
            buffer = buffer.slice(batch.length);
            persist();
            return;
        }

        if (typeof fetch !== 'function') return;

        await fetch(`${url}${COLLECT_PATH}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true
        });

        buffer = buffer.slice(batch.length);
        persist();
    } catch {
        // Left in the buffer; the next scheduled flush retries the same batch.
    } finally {
        flushInFlight = false;
    }
}

function enqueue(name: ClientEventName, props: unknown): void {
    restore();
    attachUnloadFlush();

    if (buffer.length >= BUFFER_CAP) {
        droppedSinceLastFlush += 1;
        return;
    }

    buffer.push({
        eventId: safeEventId(),
        name,
        occurredAt: new Date().toISOString(),
        props
    });
    persist();

    if (buffer.length >= FLUSH_AT_SIZE) {
        void flush();
    } else {
        scheduleFlush();
    }
}

// Bypasses the count/interval thresholds for a caller that already knows the
// page is going away right now (e.g. a checkout-abandoned listener) — those
// callers cannot rely on the next scheduled flush ever running.
function flushImmediately(): void {
    void flush(true);
}

// Exposes in-memory state for analytics.test.ts; nothing in src/ reads this.
function __testQueueState(): { length: number; dropped: number } {
    return { length: buffer.length, dropped: droppedSinceLastFlush };
}

function __resetForTests(): void {
    buffer = [];
    droppedSinceLastFlush = 0;
    flushInFlight = false;
    restoredFromStorage = false;
    listenersAttached = false;
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

export { enqueue, flushImmediately, __resetForTests, __testQueueState };
